import { useState, useEffect } from 'react'
import { AuditEvent } from '@medplum/fhirtypes'
import { Collapse, Loader } from '@mantine/core'
import InfiniteScroll from 'react-infinite-scroll-component'
import dayjs from 'dayjs'
import { DateGroupHeader } from '@/components/ui/Timeline'
import { useGroupedTimeline, DateGroup, usePatientSummary } from '@/hooks'
import { extractTitleAndIcon, isRenderableIcon } from './ChronologicalRecords.utils'
import { formatDateTime } from '@/utils'

type Props = {
  resources: AuditEvent[]
  selectedResource?: AuditEvent
  onResourceSelect: (resource: AuditEvent) => void
  hasMore: boolean
  onLoadMore: () => void
  sorting: 'asc' | 'desc'
  patientId: string
  patientName: string
  patientInfo: string
  isLoading?: boolean
}

export function GroupedTimelineView({
  resources,
  selectedResource,
  onResourceSelect,
  hasMore,
  onLoadMore,
  sorting,
  patientId,
  patientName,
  patientInfo,
  isLoading,
}: Props) {
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set())
  const groups = useGroupedTimeline(resources)
  const { fetchSummary, getState } = usePatientSummary()

  // Sort groups based on sorting direction
  const sortedGroups = [...groups].sort((a, b) => {
    return sorting === 'desc' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)
  })

  // Auto-expand the date group when a resource is selected
  useEffect(() => {
    if (selectedResource?.recorded) {
      const date = dayjs(selectedResource.recorded).format('YYYY-MM-DD')
      setExpandedDates((prev) => {
        if (prev.has(date)) return prev
        const next = new Set(prev)
        next.add(date)
        return next
      })
    }
  }, [selectedResource])

  // Auto-load more when grouped content doesn't fill the viewport
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const container = document.getElementById('scrollableDiv')
      if (!container || isLoading) return

      const hasScroll = container.scrollHeight > container.clientHeight
      if (!hasScroll && hasMore && resources.length > 0) {
        onLoadMore()
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [resources.length, hasMore, isLoading, onLoadMore])

  const toggleDateGroup = (date: string, group: DateGroup, displayDate: string) => {
    const isExpanding = !expandedDates.has(date)

    setExpandedDates((prev) => {
      const next = new Set(prev)
      if (next.has(date)) {
        next.delete(date)
      } else {
        next.add(date)
      }
      return next
    })

    // Fetch summary on expand (if not already cached/loading)
    if (isExpanding) {
      const key = `${patientId}|${date}`
      const state = getState(key)
      if (!state.summary && !state.loading) {
        // Get all items for this date group
        const items = group.resourceCounts.flatMap((rc) => rc.items)
        fetchSummary(key, patientName, patientInfo, displayDate, items)
      }
    }
  }

  if (!resources.length) {
    return (
      <div className="px-4 text-center text-sm">
        {isLoading ? 'Loading records...' : 'No records available'}
      </div>
    )
  }

  return (
    <InfiniteScroll
      dataLength={resources.length}
      next={onLoadMore}
      hasMore={hasMore}
      loader={
        <div className="flex justify-center py-4">
          <Loader color="blue" size="sm" />
        </div>
      }
      scrollableTarget="scrollableDiv"
      style={{ display: 'contents' }}
    >
      {sortedGroups.map((group) => {
        const isExpanded = expandedDates.has(group.date)

        // Get all items for this group, sorted by time
        const groupItems = group.resourceCounts.flatMap((rc) => rc.items)
        const sortedItems = [...groupItems].sort((a, b) => {
          const timeA = a.recorded || ''
          const timeB = b.recorded || ''
          return sorting === 'desc' ? timeB.localeCompare(timeA) : timeA.localeCompare(timeB)
        })

        // Get summary state for this date group
        const summaryKey = `${patientId}|${group.date}`
        const summaryState = getState(summaryKey)

        return (
          <div key={group.date}>
            <DateGroupHeader
              displayDate={group.displayDate}
              totalCount={group.totalCount}
              resourceCounts={group.resourceCounts}
              isExpanded={isExpanded}
              onToggle={() => toggleDateGroup(group.date, group, group.displayDate)}
              summary={summaryState.summary}
              summaryLoading={summaryState.loading}
              summaryError={summaryState.error}
            />

            <Collapse in={isExpanded} transitionDuration={200}>
              <div className="ml-4 border-l-2 border-l-[#D0E1F2]">
                {sortedItems.map((resource) => {
                  const linkedResource = resource.entity?.[0]?.what?.resource
                  const { title, icon: Icon, color, item } = extractTitleAndIcon(linkedResource)
                  const isSelected = resource.id === selectedResource?.id

                  if (!item) {
                    return null
                  }

                  return (
                    <div
                      key={resource.id}
                      onClick={() => onResourceSelect(resource)}
                      className={[
                        'ml-2 cursor-pointer border-b border-b-[#D0E1F2] px-4 py-2 text-[13px]',
                        isSelected ? 'bg-[#dcebfd]' : 'hover:bg-[#F0F7FF]',
                      ].join(' ')}
                    >
                      <div className="flex justify-between">
                        <p
                          className="text-primary flex-1 break-words"
                          dangerouslySetInnerHTML={{
                            __html: (title || 'Unknown').replace('(Plain text)', ''),
                          }}
                        />
                        {isRenderableIcon(Icon) && (
                          <p>
                            <Icon size={15} color={color} />
                          </p>
                        )}
                      </div>
                      <p className="text-[#7D7987]">
                        {formatDateTime(resource.recorded, '-', true)}
                      </p>
                    </div>
                  )
                })}
              </div>
            </Collapse>
          </div>
        )
      })}
    </InfiniteScroll>
  )
}
